/**
 * Entity preview data layer.
 *
 * `ViewEntityButton` warms the cache on mount (idle time) so that when the user
 * hovers, the preview renders from already-fetched data — no fetch-on-hover.
 * A module-level promise cache dedupes concurrent loads and persists across
 * mounts for the session.
 */
import { useEffect, useState } from "react";
import {
  getArtifact,
  getInteraction,
  getMission,
  getTriggerEvent,
  listAgents,
} from "@/services/api/agentic-v2";
import type {
  AgentSummary,
  Artifact,
  MissionDetail,
  ThreadInteraction,
  TriggerEventRow,
} from "@/services/api/agentic-v2/types";
import type { EntityRef } from "../shared/entityDeepLink";
import { missionDisplayLabel } from "../shared/missionDisplayLabel";

export type EntityPreviewData =
  | { kind: "trigger"; event: TriggerEventRow }
  | { kind: "mission"; mission: MissionDetail; agents: AgentSummary[] }
  | { kind: "task"; taskId: string }
  | { kind: "interaction"; interaction: ThreadInteraction }
  | { kind: "tool_call"; interaction: ThreadInteraction; stepIndex: number | null }
  | { kind: "artifact"; artifact: Artifact };

export type PreviewStatus = "idle" | "loading" | "ready" | "error";

function previewKey(ref: EntityRef): string {
  return `${ref.kind}:${ref.id}:${ref.version ?? ""}`;
}

const cache = new Map<string, Promise<EntityPreviewData>>();
const resolved = new Map<string, EntityPreviewData>();
const resolvedListeners = new Set<() => void>();

function notifyResolvedListeners(): void {
  resolvedListeners.forEach((listener) => listener());
}

/** Subscribe when any preview resolves (for label refresh in {@link EntityRichText}). */
export function subscribeEntityPreviewResolved(listener: () => void): () => void {
  resolvedListeners.add(listener);
  return () => {
    resolvedListeners.delete(listener);
  };
}

/** Return cached preview data when already loaded; otherwise null. */
export function peekEntityPreview(ref: EntityRef): EntityPreviewData | null {
  return resolved.get(previewKey(ref)) ?? null;
}

export function entityDisplayLabelFromPreview(
  ref: EntityRef,
  data: EntityPreviewData
): string | null {
  switch (data.kind) {
    case "artifact":
      return data.artifact.filename?.trim() || null;
    case "mission":
      return missionDisplayLabel(data.mission);
    case "task":
      return null;
    case "interaction":
      return `Interaction ${data.interaction.sequence_number}`;
    case "tool_call": {
      const idx = ref.version ?? data.stepIndex;
      const step = idx != null ? data.interaction.steps?.[idx] : undefined;
      const display =
        step?.tool_display_name?.trim() || step?.tool?.trim() || "";
      return display || null;
    }
    case "trigger":
      return data.event.event_type?.trim() || null;
    default:
      return null;
  }
}

async function fetchPreview(ref: EntityRef): Promise<EntityPreviewData> {
  switch (ref.kind) {
    case "trigger":
      return { kind: "trigger", event: await getTriggerEvent(ref.id) };
    case "mission": {
      const missionId = ref.scope.mission_id || ref.id;
      const [mission, agents] = await Promise.all([
        getMission(missionId),
        listAgents(),
      ]);
      return { kind: "mission", mission, agents };
    }
    case "task":
      return { kind: "task", taskId: ref.id };
    case "interaction":
      return { kind: "interaction", interaction: await getInteraction(ref.id) };
    case "tool_call":
      return {
        kind: "tool_call",
        interaction: await getInteraction(ref.id),
        stepIndex: ref.version ?? null,
      };
    case "artifact":
      return { kind: "artifact", artifact: await getArtifact(ref.id) };
    default:
      throw new Error(`Unsupported preview kind: ${(ref as EntityRef).kind}`);
  }
}

/** Start (or reuse) the load for a ref. Safe to call repeatedly. */
export function loadEntityPreview(ref: EntityRef): Promise<EntityPreviewData> {
  const key = previewKey(ref);
  const existing = resolved.get(key);
  if (existing) return Promise.resolve(existing);

  let promise = cache.get(key);
  if (!promise) {
    promise = fetchPreview(ref)
      .then((data) => {
        resolved.set(key, data);
        notifyResolvedListeners();
        return data;
      })
      .catch((err) => {
        cache.delete(key);
        throw err;
      });
    cache.set(key, promise);
  }
  return promise;
}

/** Prefetch on idle so hover is instant. No-op when already cached. */
export function prefetchEntityPreview(ref: EntityRef): void {
  const key = previewKey(ref);
  if (cache.has(key) || resolved.has(key)) return;
  const run = () => {
    void loadEntityPreview(ref).catch(() => {});
  };
  if ("requestIdleCallback" in window && typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(run);
  } else {
    window.setTimeout(run, 200);
  }
}

export interface UseEntityPreviewResult {
  status: PreviewStatus;
  data: EntityPreviewData | null;
}

/** Subscribe to a ref's preview data; fetches (or reads cache) when `enabled`. */
export function useEntityPreview(
  ref: EntityRef,
  enabled: boolean
): UseEntityPreviewResult {
  const key = previewKey(ref);
  const [result, setResult] = useState<UseEntityPreviewResult>({
    status: "idle",
    data: null,
  });

  useEffect(() => {
    if (!enabled) return;
    let mounted = true;
    setResult((prev) => (prev.status === "ready" ? prev : { status: "loading", data: null }));
    loadEntityPreview(ref)
      .then((data) => {
        if (mounted) setResult({ status: "ready", data });
      })
      .catch(() => {
        if (mounted) setResult({ status: "error", data: null });
      });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, key]);

  return result;
}
